import React from "react";
import classNames from "classnames";
import BootstrapButton from "react-bootstrap/Button";

const sizes = {
  lg: "lg",
  sm: "sm",
};

// Aliases preserved for backwards compatibility while using Bootstrap variants.
const variantAliases = {
  icon: { variant: "link", className: "bg-transparent border-0" },
  "primary-gradient": { variant: "primary", className: "bg-primary-gradient" },
  "secondary-gradient": {
    variant: "secondary",
    className: "bg-secondary-gradient",
  },
};

const bootstrapVariants = new Set([
  "primary",
  "secondary",
  "success",
  "danger",
  "warning",
  "info",
  "light",
  "dark",
  "link",
  "outline-primary",
  "outline-secondary",
  "outline-success",
  "outline-danger",
  "outline-warning",
  "outline-info",
  "outline-light",
  "outline-dark",
  "textual-primary",
  "textual-secondary",
  "textual-success",
  "textual-danger",
  "textual-warning",
  "textual-info",
  "textual-light",
  "textual-dark",
]);

const aliasVariants = new Set(Object.keys(variantAliases));

function isThemeDrivenVariant(variant) {
  if (typeof variant !== "string" || !variant.trim()) {
    return false;
  }

  // React-Bootstrap renders variant via className: btn-{variant}.
  // Accept custom theme color names and generated patterns so new colors
  // from $theme-colors (e.g. amethyst) work automatically.
  const normalized = variant.trim();
  const isSafeToken = /^[a-z0-9-]+$/i.test(normalized);

  if (!isSafeToken) {
    return false;
  }

  return (
    normalized.startsWith("outline-") ||
    normalized.startsWith("tertiary-") ||
    // Filled style: primary, brand, amethyst, etc.
    !normalized.includes("-")
  );
}

function getVariantConfig(
  variant,
  {
    strictVariantCheck = false,
    fallbackVariant = "primary",
    customVariants = [],
  } = {}
) {
  if (variantAliases[variant]) {
    return variantAliases[variant];
  }

  const customVariantSet = new Set(customVariants);
  const isKnownVariant =
    bootstrapVariants.has(variant) ||
    aliasVariants.has(variant) ||
    customVariantSet.has(variant) ||
    isThemeDrivenVariant(variant);

  if (strictVariantCheck && !isKnownVariant) {
    return { variant: fallbackVariant, className: "" };
  }

  // Pass through custom and Bootstrap variants directly (ex: brand, yellow, outline-*, etc.)
  return { variant, className: "" };
}

export default function Button({
  as = "button",
  variant = "primary",
  size = "",
  className = "",
  startIcon,
  endIcon,
  isActive = false,
  strictVariantCheck = false,
  fallbackVariant = "primary",
  customVariants = [],
  children,
  ...rest
}) {
  const { variant: resolvedVariant, className: variantClassName } =
    getVariantConfig(variant, {
      strictVariantCheck,
      fallbackVariant,
      customVariants,
    });
  const trackingValue = React.Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : ""))
    .join("")
    .trim();

  return (
    <BootstrapButton
      as={as}
      variant={resolvedVariant}
      size={sizes[size] || undefined}
      active={isActive}
      className={classNames(variantClassName, className)}
      data-tlabel={trackingValue}
      {...rest}
    >
      {startIcon && <span className="me-1 btn-start-icon">{startIcon}</span>}
      {children}
      {endIcon && <span className="ms-1 btn-end-icon">{endIcon}</span>}
    </BootstrapButton>
  );
}

/* <Button as="button" endIcon={<i className="fa fa-spinner fa-spin" />}>
    Click
</Button> */
